const MAX_DPR = 2;
const ROAD_WIDTH = 12;
const ROAD_BORDER_WIDTH = 16;
const CAR_LENGTH = 14;
const CAR_WIDTH = 7;

interface Point {
  x: number;
  y: number;
}

interface Segment {
  start: Point;
  end: Point;
  length: number;
  distance: number;
  angle: number;
}

interface Route {
  points: Point[];
  segments: Segment[];
  length: number;
}

interface Car {
  route: number;
  distance: number;
  speed: number;
  direction: 1 | -1;
  color: number;
}

interface HslColor {
  h: number;
  s: number;
  l: number;
}

const NORMALIZED_ROUTES: readonly (readonly Point[])[] = [
  [
    { x: -0.04, y: 0.18 },
    { x: 0.24, y: 0.18 },
    { x: 0.24, y: 0.38 },
    { x: 0.68, y: 0.38 },
    { x: 0.68, y: 0.19 },
    { x: 1.04, y: 0.19 },
  ],
  [
    { x: 0.12, y: -0.04 },
    { x: 0.12, y: 0.28 },
    { x: 0.36, y: 0.28 },
    { x: 0.36, y: 0.7 },
    { x: 0.58, y: 0.7 },
    { x: 0.58, y: 1.04 },
  ],
  [
    { x: -0.04, y: 0.78 },
    { x: 0.18, y: 0.78 },
    { x: 0.18, y: 0.57 },
    { x: 0.82, y: 0.57 },
    { x: 0.82, y: 0.82 },
    { x: 1.04, y: 0.82 },
  ],
  [
    { x: 0.78, y: -0.04 },
    { x: 0.78, y: 0.31 },
    { x: 0.54, y: 0.31 },
    { x: 0.54, y: 0.86 },
    { x: 0.74, y: 0.86 },
    { x: 0.74, y: 1.04 },
  ],
  [
    { x: -0.04, y: 0.46 },
    { x: 0.08, y: 0.46 },
    { x: 0.08, y: 0.9 },
    { x: 0.42, y: 0.9 },
    { x: 0.42, y: 1.04 },
  ],
  [
    { x: 1.04, y: 0.48 },
    { x: 0.91, y: 0.48 },
    { x: 0.91, y: 0.67 },
    { x: 0.66, y: 0.67 },
    { x: 0.66, y: 1.04 },
  ],
];

const DESTINATIONS = [
  { route: 0, point: 2, color: 0, shape: 'circle' },
  { route: 1, point: 3, color: 1, shape: 'diamond' },
  { route: 2, point: 3, color: 2, shape: 'circle' },
  { route: 3, point: 2, color: 3, shape: 'diamond' },
  { route: 4, point: 2, color: 1, shape: 'circle' },
  { route: 5, point: 2, color: 0, shape: 'diamond' },
] as const;

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(Math.max(value, minimum), maximum);
}

function rgbToHsl(red: number, green: number, blue: number): HslColor {
  let r = red / 255;
  let g = green / 255;
  let b = blue / 255;
  let maximum = Math.max(r, g, b);
  let minimum = Math.min(r, g, b);
  let lightness = (maximum + minimum) / 2;
  let delta = maximum - minimum;
  if (delta === 0) return { h: 210, s: 0, l: lightness * 100 };

  let saturation =
    delta / (lightness > 0.5 ? 2 - maximum - minimum : maximum + minimum);
  let hue = 0;
  if (maximum === r) hue = (g - b) / delta + (g < b ? 6 : 0);
  if (maximum === g) hue = (b - r) / delta + 2;
  if (maximum === b) hue = (r - g) / delta + 4;
  return { h: hue * 60, s: saturation * 100, l: lightness * 100 };
}

function readCanvasColor(canvas: HTMLCanvasElement): HslColor {
  let parser = document.createElement('canvas');
  parser.width = 1;
  parser.height = 1;
  let parserContext = parser.getContext('2d', { willReadFrequently: true });
  if (!parserContext) return { h: 220, s: 80, l: 56 };
  parserContext.fillStyle = getComputedStyle(canvas).color;
  parserContext.fillRect(0, 0, 1, 1);
  let color = parserContext.getImageData(0, 0, 1, 1).data;
  return rgbToHsl(color[0], color[1], color[2]);
}

function initializeRoadNetwork(canvas: HTMLCanvasElement): void {
  let context = canvas.getContext('2d');
  if (!context) return;

  let width = 0;
  let height = 0;
  let dpr = 1;
  let routes: Route[] = [];
  let cars: Car[] = [];
  let palette: string[] = [];
  let roadColor = '';
  let roadBorderColor = '';
  let laneColor = '';
  let carWindowColor = '';
  let carWheelColor = '';
  let carLightColor = '';
  let animationFrame = 0;
  let previousFrameAt = 0;
  let isVisible = true;
  let reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function updateColors(): void {
    let primary = readCanvasColor(canvas);
    let isDark = document.documentElement.classList.contains('dark');
    let saturation = clamp(primary.s || 72, 52, 82);
    let lightness = isDark ? 66 : 53;
    let offsets = [0, 38, -38, 180];
    palette = offsets.map(
      (offset) =>
        `hsl(${(primary.h + offset + 360) % 360} ${saturation}% ${lightness}%)`
    );
    roadBorderColor = isDark
      ? 'rgba(2, 6, 23, 0.46)'
      : 'rgba(203, 213, 225, 0.42)';
    roadColor = isDark
      ? 'rgba(71, 85, 105, 0.55)'
      : 'rgba(241, 245, 249, 0.94)';
    laneColor = isDark
      ? 'rgba(226, 232, 240, 0.16)'
      : 'rgba(100, 116, 139, 0.16)';
    carWindowColor = isDark
      ? 'rgba(15, 23, 42, 0.78)'
      : 'rgba(30, 41, 59, 0.72)';
    carWheelColor = isDark
      ? 'rgba(2, 6, 23, 0.9)'
      : 'rgba(15, 23, 42, 0.8)';
    carLightColor = isDark
      ? 'rgba(255, 255, 255, 0.9)'
      : 'rgba(255, 255, 255, 0.95)';
  }

  function createRoute(normalizedPoints: readonly Point[]): Route {
    let points = normalizedPoints.map((point) => ({
      x: point.x * width,
      y: point.y * height,
    }));
    let segments: Segment[] = [];
    let routeLength = 0;

    for (let index = 1; index < points.length; index += 1) {
      let start = points[index - 1];
      let end = points[index];
      let deltaX = end.x - start.x;
      let deltaY = end.y - start.y;
      let length = Math.hypot(deltaX, deltaY);
      segments.push({
        start,
        end,
        length,
        distance: routeLength,
        angle: Math.atan2(deltaY, deltaX),
      });
      routeLength += length;
    }

    return { points, segments, length: routeLength };
  }

  function createCars(): void {
    let targetCount = width < 640 ? 14 : 22;
    cars = new Array<Car>(targetCount);
    for (let index = 0; index < targetCount; index += 1) {
      let routeIndex = index % routes.length;
      let route = routes[routeIndex];
      cars[index] = {
        route: routeIndex,
        distance: ((index * 0.61803398875) % 1) * route.length,
        speed: 20 + ((index * 17) % 15),
        direction: index % 3 === 0 ? -1 : 1,
        color: (index + routeIndex) % palette.length,
      };
    }
  }

  function traceRoute(route: Route): void {
    context.beginPath();
    context.moveTo(route.points[0].x, route.points[0].y);
    for (let index = 1; index < route.points.length; index += 1) {
      context.lineTo(route.points[index].x, route.points[index].y);
    }
    context.stroke();
  }

  function drawRoads(): void {
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.setLineDash([]);
    context.strokeStyle = roadBorderColor;
    context.lineWidth = ROAD_BORDER_WIDTH;
    for (let route of routes) traceRoute(route);

    context.strokeStyle = roadColor;
    context.lineWidth = ROAD_WIDTH;
    for (let route of routes) traceRoute(route);

    context.strokeStyle = laneColor;
    context.lineWidth = 1;
    context.setLineDash([2, 8]);
    for (let route of routes) traceRoute(route);
    context.setLineDash([]);
  }

  function pointOnRoute(route: Route, distance: number): Point & { angle: number } {
    let wrappedDistance = ((distance % route.length) + route.length) % route.length;
    let segment = route.segments[route.segments.length - 1];
    for (let candidate of route.segments) {
      if (wrappedDistance <= candidate.distance + candidate.length) {
        segment = candidate;
        break;
      }
    }
    let progress = clamp(
      (wrappedDistance - segment.distance) / segment.length,
      0,
      1
    );
    return {
      x: segment.start.x + (segment.end.x - segment.start.x) * progress,
      y: segment.start.y + (segment.end.y - segment.start.y) * progress,
      angle: segment.angle,
    };
  }

  function drawDestinations(): void {
    for (let destination of DESTINATIONS) {
      let point = routes[destination.route]?.points[destination.point];
      if (!point) continue;
      context.save();
      context.translate(point.x, point.y);
      context.fillStyle = roadBorderColor;
      context.beginPath();
      context.arc(0, 0, 12, 0, Math.PI * 2);
      context.fill();
      context.fillStyle = palette[destination.color];

      if (destination.shape === 'diamond') {
        context.rotate(Math.PI / 4);
        context.fillRect(-7, -7, 14, 14);
        context.fillStyle = roadColor;
        context.fillRect(-3, -3, 6, 6);
      } else {
        context.beginPath();
        context.arc(0, 0, 8, 0, Math.PI * 2);
        context.fill();
        context.fillStyle = roadColor;
        context.beginPath();
        context.arc(0, 0, 3, 0, Math.PI * 2);
        context.fill();
      }
      context.restore();
    }
  }

  function drawCars(): void {
    for (let car of cars) {
      let position = pointOnRoute(routes[car.route], car.distance);
      context.save();
      context.translate(position.x, position.y);
      context.rotate(position.angle + (car.direction < 0 ? Math.PI : 0));

      context.fillStyle = 'rgba(15, 23, 42, 0.2)';
      context.beginPath();
      context.roundRect(
        -CAR_LENGTH * 0.5 + 1,
        -CAR_WIDTH * 0.5 + 1,
        CAR_LENGTH,
        CAR_WIDTH,
        2
      );
      context.fill();

      context.fillStyle = carWheelColor;
      context.fillRect(-4.5, -4, 3, 1.5);
      context.fillRect(2, -4, 3, 1.5);
      context.fillRect(-4.5, 2.5, 3, 1.5);
      context.fillRect(2, 2.5, 3, 1.5);

      context.fillStyle = palette[car.color];
      context.beginPath();
      context.roundRect(
        -CAR_LENGTH * 0.5,
        -CAR_WIDTH * 0.5,
        CAR_LENGTH,
        CAR_WIDTH,
        2
      );
      context.fill();

      context.fillStyle = carWindowColor;
      context.beginPath();
      context.roundRect(-2.5, -2.4, 5.5, 4.8, 1);
      context.fill();

      context.strokeStyle = 'rgba(255, 255, 255, 0.35)';
      context.lineWidth = 0.75;
      context.beginPath();
      context.moveTo(1.1, -2.25);
      context.lineTo(1.1, 2.25);
      context.stroke();

      context.fillStyle = carLightColor;
      context.fillRect(5.4, -2.3, 1, 1.2);
      context.fillRect(5.4, 1.1, 1, 1.2);
      context.restore();
    }
  }

  function drawFrame(): void {
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawRoads();
    drawDestinations();
    drawCars();
  }

  function animate(now: number): void {
    animationFrame = 0;
    if (!isVisible || reducedMotion.matches) return;
    let deltaTime = previousFrameAt ? Math.min(now - previousFrameAt, 50) : 16.67;
    previousFrameAt = now;
    for (let car of cars) {
      car.distance += car.speed * car.direction * (deltaTime / 1000);
    }
    drawFrame();
    animationFrame = requestAnimationFrame(animate);
  }

  function requestFrame(): void {
    if (animationFrame !== 0 || !isVisible || reducedMotion.matches) return;
    previousFrameAt = 0;
    animationFrame = requestAnimationFrame(animate);
  }

  function resize(): void {
    let bounds = canvas.getBoundingClientRect();
    let nextWidth = Math.max(1, Math.round(bounds.width));
    let nextHeight = Math.max(1, Math.round(bounds.height));
    let nextDpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
    if (nextWidth === width && nextHeight === height && nextDpr === dpr) return;

    width = nextWidth;
    height = nextHeight;
    dpr = nextDpr;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    routes = NORMALIZED_ROUTES.map(createRoute);
    updateColors();
    createCars();
    drawFrame();
  }

  let resizeObserver = new ResizeObserver(resize);
  resizeObserver.observe(canvas);

  let themeObserver = new MutationObserver(() => {
    updateColors();
    drawFrame();
  });
  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  });

  let intersectionObserver = new IntersectionObserver((entries) => {
    isVisible = entries[0]?.isIntersecting ?? true;
    if (isVisible) {
      resize();
      requestFrame();
      return;
    }
    if (animationFrame !== 0) cancelAnimationFrame(animationFrame);
    animationFrame = 0;
    previousFrameAt = 0;
  });
  intersectionObserver.observe(canvas);

  reducedMotion.addEventListener('change', () => {
    if (reducedMotion.matches) {
      if (animationFrame !== 0) cancelAnimationFrame(animationFrame);
      animationFrame = 0;
      previousFrameAt = 0;
      drawFrame();
      return;
    }
    requestFrame();
  });

  resize();
  requestFrame();
}

function initializeRoadNetworks(): void {
  let canvases = document.querySelectorAll<HTMLCanvasElement>(
    '[data-road-network]'
  );
  for (let canvas of canvases) initializeRoadNetwork(canvas);
}

initializeRoadNetworks();
